import React from 'react';
import XIcon from './icons/XIcon';
import TrashIcon from './icons/TrashIcon';

interface ConfirmationModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    confirmText?: string;
    cancelText?: string;
    type?: 'danger' | 'info' | 'warning';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ 
    isOpen, 
    title, 
    message, 
    onConfirm, 
    onCancel, 
    confirmText = 'OK', 
    cancelText = 'Cancel',
    type = 'danger'
}) => {
    if (!isOpen) return null;

    const typeColors = {
        danger: 'bg-red-600 hover:bg-red-700 text-white',
        info: 'bg-sky-600 hover:bg-sky-700 text-white',
        warning: 'bg-amber-500 hover:bg-amber-600 text-white'
    };

    const iconColors = {
        danger: 'text-red-100 bg-red-600',
        info: 'text-sky-100 bg-sky-600',
        warning: 'text-amber-100 bg-amber-500'
    };

    return (
        <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[500] p-4 animate-fade-in"
            onClick={(e) => {
                e.stopPropagation();
                onCancel();
            }}
        >
            <div 
                className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-[340px] overflow-hidden transform transition-all border border-slate-100 dark:border-slate-800"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-8 pb-6 text-center">
                    {type === 'danger' && (
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-50 dark:bg-red-900/20 text-red-500 mb-6">
                            <TrashIcon className="h-8 w-8" />
                        </div>
                    )}
                    {type === 'warning' && (
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-500 mb-6">
                            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 15c-.77 1.333.192 3 1.732 3z" /></svg>
                        </div>
                    )}
                    <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 mb-3">{title}</h3>
                    <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                        {message}
                    </p>
                </div>
                <div className="p-4 pt-0 flex gap-3">
                    <button 
                        onClick={(e) => { e.stopPropagation(); onCancel(); }}
                        className="flex-1 px-4 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-sm"
                    >
                        {cancelText}
                    </button>
                    <button 
                        onClick={(e) => { e.stopPropagation(); onConfirm(); }}
                        className={`flex-1 px-4 py-3 font-bold rounded-xl shadow-sm transition-colors text-sm ${
                            type === 'danger' ? 'bg-red-600 hover:bg-red-700 text-white' :
                            type === 'warning' ? 'bg-amber-500 hover:bg-amber-600 text-white' :
                            'bg-teal-600 hover:bg-teal-700 text-white'
                        }`}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;